import { getCommercialCategories } from "@/services";
import { useService } from "@/hooks/useService";
import { CommercialCategory } from "@/services/mantenimientoComercios/types";
import { Check } from "@material-ui/icons";
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";

type EditCommercialCategoryDialogProps = {
  currentCategory?: string | null;
  categoryModalOpen: boolean;
  setCategoryModalOpen: (open: boolean) => void;
  handleSaveCategory: (newCategory: string) => void;
};

const EditCommercialCategoryDialog = ({
  currentCategory = "",
  categoryModalOpen,
  setCategoryModalOpen,
  handleSaveCategory,
}: EditCommercialCategoryDialogProps) => {
  const { execute, loading } = useService<CommercialCategory[], undefined>(
    getCommercialCategories,
  );

  const [selectedCategory, setSelectedCategory] =
    useState<CommercialCategory | null>(null);
  const [availableCategories, setAvailableCategories] = useState<
    CommercialCategory[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (currentCategory && availableCategories.length > 0) {
      const category = availableCategories.find(
        (cat) => cat.description === currentCategory,
      );
      setSelectedCategory(category || null);
    }
  }, [currentCategory, availableCategories]);

  useEffect(() => {
    const fetchData = async () => {
      setSearchValue("");

      const result = await execute(undefined);
      if (result && Array.isArray(result)) {
        setAvailableCategories(result);
      } else {
        // Handle error
        console.error("Error fetching commercial categories", result);
        setCategoryModalOpen(false);
      }
    };

    if (categoryModalOpen) fetchData();
  }, [categoryModalOpen]);

  return !categoryModalOpen ? null : (
    <>
      <Dialog
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Categoría Comercial</DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cargando categorías...
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccione la nueva categoría para el comercio
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar categoría..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{ mb: 2 }}
              />
              <List
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {availableCategories
                  .filter((cat) =>
                    cat.description
                      .toLowerCase()
                      .includes(searchValue ? searchValue.toLowerCase() : ""),
                  )
                  .map((category, index) => (
                    <Box key={category.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedCategory === category}
                          onClick={() => setSelectedCategory(category)}
                        >
                          <ListItemText primary={category.description} />
                          {selectedCategory === category && (
                            <Check color="primary" fontSize="small" />
                          )}
                        </ListItemButton>
                      </ListItem>
                      {index < availableCategories.length - 1 && <Divider />}
                    </Box>
                  ))}
              </List>
              {selectedCategory && (
                <Box
                  sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Categoría seleccionada:
                  </Typography>
                  <Chip
                    label={selectedCategory.description}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              handleSaveCategory(selectedCategory?.id || "");
            }}
            variant="contained"
            disabled={!selectedCategory || loading}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCommercialCategoryDialog;
